// ============================================================================
// JARVISTRAVEL - TRIP ALERTS
// What the trip is telling you right now, as data.
//
// Extracted from AlertBanner (JAR-2552) because two surfaces need the same
// answer: the card that shows an alert once, and the bell that holds it
// afterwards. Computing it twice is how the two drift, and a bell that
// disagrees with the card is worse than either alone.
//
// These are CONDITIONS, not events. Every one is derived from the trip in
// memory and recomputed on each render, so an alert stops being true the
// moment its cause does — raise the budget and "Trending over" is simply gone.
// That is also why they are not core notifications: a stored row would go on
// asserting something that had stopped being true, and core cannot compute any
// of these without the budget and Fatigue Index engines that live here.
// ============================================================================

import { Wallet, Activity, CalendarDays, CreditCard, type LucideIcon } from 'lucide-react';
import type { Trip } from '@/app/types';
import { summarizeBudget, type BudgetSummary } from '@/app/utils/budgetEngine';
import { budgetStatusIsActionable, budgetStatusLabel } from '@/app/utils/budget';
import { todayYmd } from '@/app/utils/dateOnly';
import { fiBand } from '@/app/utils/fi';
import { formatCurrency } from '@/app/utils';
import { dayScore } from '@/app/utils/fiEngine';

export type AlertVariant = 'danger' | 'warning' | 'info' | 'success';

/** Which action an alert offers. The handler lives with whoever renders it. */
export type AlertAction = 'review-budget' | 'set-budget' | 'view-itinerary';

export interface TripAlert {
  id: string;
  variant: AlertVariant;
  icon: LucideIcon;
  title: string;
  message: string;
  action?: { label: string; kind: AlertAction };
}

/**
 * tripAlerts is every alert true of this trip right now, in display order.
 *
 * Pure: no storage, no React, no decision about whether an alert has been seen.
 * Whether to show one as a card is the caller's business (see alertState).
 */
// A trip still worth nudging about. TripStatus is Draft | Active | Archived |
// Completed, and the two "you have not set this up yet" alerts used to exclude
// only Draft — so a trip that had already happened still said "Set a trip
// budget to track your spending", which is advice about a past event
// (review 10-05).
const stillPlannable = (trip: Trip) => trip.status === 'Active';

export function tripAlerts(trip: Trip, serverSummary?: BudgetSummary): TripAlert[] {
  const alerts: TripAlert[] = [];
  // Every amount is the trip's own currency. The local formatter this replaced
  // hardcoded en-US/USD, so a euro-budgeted trip read "$X over your $Y budget"
  // directly below a now-euro-aware forecast card (#578 review).
  const currency = trip.budget.currency;

  // 1. Predictive budget alert — projected overage/exhaustion, not "% used"
  //    (spec Journey 11). Only fires on an actionable trend.
  //    On a core-backed trip the caller hands in core's summary (JAR-2935):
  //    the projection, the trend and the driver are the server's, not the
  //    device engine's. Only when none was answered — a local/demo trip (the
  //    demo exception, JAR-2892), or a server failure — does the device
  //    engine compute it, as before.
  const budgetSummary = serverSummary ?? summarizeBudget(trip);
  // Branch on the number the user will READ. formatCurrency rounds to whole
  // units app-wide, so a projected overage of 0.40 fired this alert and then
  // rendered "projected $0 over your $5,000 budget" — a warning that denies
  // itself in its own sentence (#578 review).
  //
  // Ceil, not round. Rounding fixed the contradiction by SUPPRESSING the alert
  // below half a unit, which also suppressed a genuinely exhausted budget:
  // 'budget_exhausted' fires when actuals already exceed the total, and 40
  // cents over is still over (#578 review round 2).
  const projectedOver = Math.ceil(budgetSummary.projected_delta);
  if (budgetStatusIsActionable(budgetSummary.status) && projectedOver > 0) {
    // The projection excludes stops priced in another currency (costCountsIn
    // refuses to mix) — the same exclusion the forecast card discloses. When
    // any are uncounted, note it here too, so the alert doesn't state a
    // projection on incomplete data with nothing saying so (JAR-2207).
    const uncounted = budgetSummary.uncounted_items;
    const uncountedNote = uncounted > 0
      ? ` (${uncounted === 1 ? '1 stop' : `${uncounted} stops`} in another currency ${uncounted === 1 ? 'isn’t' : 'aren’t'} counted)`
      : '';
    alerts.push({
      id: 'budget-forecast',
      variant: budgetSummary.status === 'budget_exhausted' ? 'danger' : 'warning',
      icon: Wallet,
      title: budgetStatusLabel(budgetSummary.status),
      // "projected" is wrong for budget_exhausted: that status fires when
      // ACTUALS already exceed the total, so the money is spent, not forecast.
      // Saying "projected" about money already gone understates it
      // (review 10-05).
      message: budgetSummary.status === 'budget_exhausted'
        ? `${budgetSummary.primary_driver}: ${formatCurrency(projectedOver, currency)} over your ${formatCurrency(budgetSummary.total_budget, currency)} budget${uncountedNote}.`
        : `${budgetSummary.primary_driver}: projected ${formatCurrency(projectedOver, currency)} over your ${formatCurrency(budgetSummary.total_budget, currency)} budget${uncountedNote}.`,
      action: { label: 'Review budget', kind: 'review-budget' },
    });
  }

  // 2. Today comes up packed. Reads today's own day, rated by the same dayScore
  //    as the pacing strip. The trip's overall index says nothing about today,
  //    and a trip that hasn't started has no today (JAR-1442).
  //    Gated on stillPlannable like the other advisory alerts: "lighten today's
  //    load" is advice to re-plan, which a trip that is no longer Active can't
  //    act on. Reachable on a trip marked Completed while today is still one of
  //    its days (completed early) (JAR-2634).
  const today = trip.itinerary.find((d) => d.date === todayYmd());
  const todayScore = today && dayScore(today);
  if (todayScore && fiBand(todayScore) === 'packed' && stillPlannable(trip)) {
    alerts.push({
      id: 'high-fi',
      variant: 'warning',
      icon: Activity,
      title: 'Packed Day',
      message: `Today comes up packed: ${todayScore} of 9 on the Fatigue Index. Consider lightening the load.`,
      action: { label: 'View Itinerary', kind: 'view-itinerary' },
    });
  }

  // 3. Empty itinerary
  const hasItems = trip.itinerary.some((day) => day.activities.length > 0);
  if (!hasItems && stillPlannable(trip)) {
    alerts.push({
      id: 'empty-itinerary',
      variant: 'info',
      icon: CalendarDays,
      title: 'No Itinerary Items',
      message: 'This trip has no activities yet. Start planning to build your itinerary.',
    });
  }

  // 4. Budget not set
  if (trip.budget.total === 0 && stillPlannable(trip)) {
    alerts.push({
      id: 'no-budget',
      variant: 'info',
      icon: CreditCard,
      title: 'Budget Not Set',
      message: 'Set a trip budget to track your spending.',
      action: { label: 'Set Budget', kind: 'set-budget' },
    });
  }

  return alerts;
}
